import React, { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFile, faFilePdf, faFileLines, faUpload, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { PaginationControl } from 'react-bootstrap-pagination-control';
import { SiteContext } from '../../Contexts';
import { FileItem } from '../../Models/Models';
import { FileService } from '../../Services/FileService';

const FILES_PER_PAGE = 8;
const ALLOWED_EXTENSIONS = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.odt,.ods,.odp';

const FileSelectorComponent = (props: {
    handleFileClick: (file: FileItem) => void;
    selectedFilePath?: string;
    accentColor: string;
}) => {
    const { filterFiles, uploadFiles } = FileService();
    const { currentSite }: any = useContext(SiteContext);

    const [files, setFiles] = useState<FileItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    const handleLoad = async (pageNum: number) => {
        if (currentSite.id === -1) return;
        setLoading(true);
        const data = await filterFiles({
            websiteId: currentSite.id,
            limit: FILES_PER_PAGE,
            offset: (pageNum - 1) * FILES_PER_PAGE,
        });
        if (data) {
            setFiles(data.files ?? []);
            setTotalCount(data.totalCount ?? 0);
        }
        setLoading(false);
    };

    useEffect(() => {
        void handleLoad(1);
        setPage(1);
    }, [currentSite]);

    const handlePageChange = (newPageNum: number) => {
        setPage(newPageNum);
        void handleLoad(newPageNum);
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        setUploading(true);
        const fd = new FormData();
        fd.append('websiteId', currentSite.id.toString());
        fd.append('files', file, file.name);
        const uploaded = await uploadFiles(fd, true);
        setUploading(false);

        if (uploaded && uploaded.length > 0) {
            props.handleFileClick(uploaded[0]);
        }
    };

    return (
        <>
            <label
                className={`st-file-selector-upload${uploading ? ' st-file-selector-upload--uploading' : ''}`}
                style={{
                    '--file-accent': props.accentColor,
                    '--file-border': props.accentColor + '80',
                    '--file-tint': props.accentColor + '0A',
                } as React.CSSProperties}
            >
                <FontAwesomeIcon icon={uploading ? faSpinner : faUpload} spin={uploading} />
                {uploading ? 'Nahrávam...' : 'Nahrať a vybrať nový súbor'}
                <input
                    type="file"
                    accept={ALLOWED_EXTENSIONS}
                    onChange={handleUpload}
                    disabled={uploading}
                    className="d-none"
                />
            </label>

            <div
                className="st-file-selector-list"
                style={{ '--file-accent': props.accentColor, '--file-row-tint': props.accentColor + '12' } as React.CSSProperties}
            >
                {loading ? (
                    <p className="text-center py-4 text-secondary">Načítavam...</p>
                ) : files.length === 0 ? (
                    <p className="text-center py-4 text-secondary">Žiadne súbory</p>
                ) : (
                    <div className="d-flex flex-column gap-2">
                        {files.map(file => {
                            const ext = file.fileName.split('.').pop()?.toLowerCase() ?? '';
                            const { fileIcon, iconClass } = getFileStyle(ext);
                            const isSelected = props.selectedFilePath === file.filePath;
                            return (
                                <div
                                    key={file.id}
                                    onClick={() => props.handleFileClick(file)}
                                    className={`st-file-row${isSelected ? ' st-file-row--selected' : ''}`}
                                >
                                    <div className={`st-file-icon-badge ${iconClass}`}>
                                        <FontAwesomeIcon icon={fileIcon} />
                                    </div>
                                    <span className="st-file-row-name">
                                        {file.fileName}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {totalCount > FILES_PER_PAGE && (
                <div className="mt-3">
                    <PaginationControl
                        page={page}
                        between={4}
                        total={totalCount}
                        limit={FILES_PER_PAGE}
                        changePage={(p) => handlePageChange(p)}
                        ellipsis={1}
                    />
                </div>
            )}
        </>
    );
};

function getFileStyle(ext: string): { fileIcon: any; iconClass: string } {
    switch (ext) {
        case 'pdf':  return { fileIcon: faFilePdf,   iconClass: 'st-file-icon-badge--pdf' };
        case 'doc':
        case 'docx': return { fileIcon: faFileLines, iconClass: 'st-file-icon-badge--doc' };
        case 'xls':
        case 'xlsx': return { fileIcon: faFileLines, iconClass: 'st-file-icon-badge--xls' };
        case 'ppt':
        case 'pptx': return { fileIcon: faFileLines, iconClass: 'st-file-icon-badge--ppt' };
        case 'txt':
        case 'csv':  return { fileIcon: faFileLines, iconClass: 'st-file-icon-badge--txt' };
        default:     return { fileIcon: faFile,      iconClass: 'st-file-icon-badge--default' };
    }
}

export default FileSelectorComponent;
